import { atom } from "jotai";

import type {
  Absence,
  AbsenceDraft,
  AbsenceStatus,
  ValidatedHoursNotice,
} from "../types";
import { draftFromAbsence } from "../utils/draft";
import { pointsAt, sameCaseIds } from "../utils/sameCase";
import { absenceManagerAtom } from "./state";

export const isVisibleAtom = atom((get) => get(absenceManagerAtom).isVisible);

export const employeeIdAtom = atom((get) => get(absenceManagerAtom).employeeId);

export const employeeNameAtom = atom(
  (get) => get(absenceManagerAtom).employeeName,
);

export const employeeAvatarUrlAtom = atom(
  (get) => get(absenceManagerAtom).employeeAvatarUrl,
);

export const loadingAtom = atom((get) => get(absenceManagerAtom).loading);

export const savingAtom = atom((get) => get(absenceManagerAtom).saving);

export const errorAtom = atom((get) => get(absenceManagerAtom).error);

export const balanceAtom = atom((get) => get(absenceManagerAtom).balance);

export const balanceLoadingAtom = atom(
  (get) => get(absenceManagerAtom).balanceLoading,
);

export const absenceTypesAtom = atom((get) => get(absenceManagerAtom).types);

export const tabAtom = atom((get) => get(absenceManagerAtom).tab);

export const selectedIdAtom = atom((get) => get(absenceManagerAtom).selectedId);

export const absencesAtom = atom((get) => get(absenceManagerAtom).absences);

export const tabCountsAtom = atom((get) => {
  const counts: Record<AbsenceStatus, number> = {
    current: 0,
    future: 0,
    past: 0,
  };

  for (const absence of get(absencesAtom)) {
    counts[absence.status] += 1;
  }

  return counts;
});

export const visibleAbsencesAtom = atom((get) => {
  const tab = get(tabAtom);

  return get(absencesAtom).filter((absence) => absence.status === tab);
});

export const selectedAbsenceAtom = atom((get): Absence | null => {
  const selectedId = get(selectedIdAtom);

  return get(absencesAtom).find((absence) => absence.id === selectedId) ?? null;
});

export const selectedDraftAtom = atom((get): AbsenceDraft | null => {
  const absence = get(selectedAbsenceAtom);

  if (!absence) {
    return null;
  }

  return (
    get(absenceManagerAtom).drafts[absence.id] ?? draftFromAbsence(absence)
  );
});

export const selectedTypeAtom = atom((get) => {
  const draft = get(selectedDraftAtom);

  return (
    get(absenceTypesAtom).find((type) => type.id === draft?.typeId) ?? null
  );
});

/**
 * The absences the selected one may be declared a continuation of: those of a
 * type that forms cases, itself excluded.
 *
 * An absence that is not stored yet is offered too. It has no identifier to be
 * named by, so the save orders the drafts and substitutes the real identifier
 * once the server has given one; see utils/saveOrder.
 *
 * What is left out is anything whose own chain already reaches the selected
 * absence: picking it would close the chain on itself, which every walk of the
 * chain would then follow forever.
 */
export const sameCaseCandidatesAtom = atom((get): Absence[] => {
  const selectedId = get(selectedIdAtom);

  if (null === selectedId) {
    return [];
  }

  const absences = get(absencesAtom);
  const linkableTypeIds = new Set(
    get(absenceTypesAtom)
      .filter((type) => type.caseLinkable)
      .map((type) => type.id),
  );

  return absences.filter(
    (absence) =>
      absence.id !== selectedId &&
      null !== absence.typeId &&
      linkableTypeIds.has(absence.typeId) &&
      !pointsAt(absences, absence.id, selectedId),
  );
});

export const dirtyIdsAtom = atom((get) =>
  Object.keys(get(absenceManagerAtom).drafts).map(Number),
);

export const isDirtyAtom = atom((get) => get(dirtyIdsAtom).length > 0);

export const selectedSlotsAtom = atom((get) => {
  const selectedId = get(selectedIdAtom);

  return null === selectedId
    ? []
    : (get(absenceManagerAtom).slots[selectedId] ?? []);
});

export const selectedSlotsLoadingAtom = atom((get) => {
  const state = get(absenceManagerAtom);

  if (state.loading) {
    return true;
  }

  const selectedId = state.selectedId;

  return null !== selectedId && state.loadingSlots.includes(selectedId);
});

/** Null while nothing is selected, or when the period holds no validated hour. */
export const selectedValidatedHoursAtom = atom(
  (get): ValidatedHoursNotice | null => {
    const state = get(absenceManagerAtom);
    const selectedId = state.selectedId;

    if (null === selectedId) {
      return null;
    }

    const notice = state.validatedHours[selectedId];

    return notice && 0 < notice.hourCount ? notice : null;
  },
);

/**
 * The other absences of the selected one's case.
 *
 * Reading the chain from the list rather than from the drafts is deliberate:
 * patching a draft mirrors the new link onto the row, so an unsaved re-link
 * highlights straight away.
 */
export const sameCaseIdsAtom = atom((get): Set<number> =>
  sameCaseIds(get(absencesAtom), get(selectedIdAtom)),
);
